import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from '../../LanguageContext';
import styles from './Me.module.css';
import narkisImage from "../../images/נרקיס ללא רקע.webp";
import migdal22 from '../../images/יוקרה במגדל - דירת יוקרה אורבנית 22.webp';
import ramatChen6 from '../../images/בית פרטי ברמת חן -בית הפליסה 6.webp';

const NarkisProfile = () => {
  const { t, isEnglish } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);
  
  // Refs for scroll reveal
  const imageRef = useRef(null);
  const headerRef = useRef(null);
  const storyRef = useRef(null);
  
  // Refs for parallax images
  const parallax1Ref = useRef(null);
  const parallax2Ref = useRef(null);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Parallax scroll effect
  useEffect(() => {
    if (!isMobile) return;
    
    const handleScroll = () => {
      const parallaxElements = [parallax1Ref.current, parallax2Ref.current];
      
      parallaxElements.forEach((el) => {
        if (!el) return;
        
        const rect = el.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        // Check if element is in viewport
        if (rect.top < windowHeight && rect.bottom > 0) {
          // Calculate how far through the viewport the element is
          const scrollProgress = (windowHeight - rect.top) / (windowHeight + rect.height);
          // Move the image (negative value moves up, creating parallax)
          const translateY = (scrollProgress - 0.5) * 80; // Adjust 80 for intensity
          
          const img = el.querySelector('img');
          if (img) {
            img.style.transform = `translateY(${translateY}px)`;
          }
        }
      });
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial call
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  // Scroll reveal animation
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add(styles.visible);
        }
      });
    }, observerOptions);

    const elements = [imageRef, headerRef, storyRef];
    elements.forEach((ref) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      elements.forEach((ref) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);

  // Parallax divider component for mobile
  const ParallaxDivider = React.forwardRef(({ image, alt }, ref) => (
    <div className={styles.parallaxDivider} ref={ref}>
      <img src={image} alt={alt} className={styles.parallaxImage} loading="lazy" decoding="async" />
      <div className={styles.parallaxOverlay} />
    </div>
  ));

  // Regular divider for desktop
  const RegularDivider = () => (
    <div className={styles.divider}>
      <span className={styles.dividerDot}></span>
    </div>
  );

  return (
    <section className={styles.profileSection} id="about" aria-label="אודות נרקיס רובין ברזני">
      {/* Decorative corner elements */}
      <div className={`${styles.decorCorner} ${styles.topRight}`}></div>
      <div className={`${styles.decorCorner} ${styles.bottomLeft}`}></div>
      
      <div className={styles.container}>
        <div className={styles.leftColumn}>
          <div className={`${styles.imageSection} ${styles.fadeInLeft}`} ref={imageRef}>
            <div className={styles.imageFrame}>
              <div className={styles.profileImage}>
                <img src={narkisImage} alt="נרקיס רובין ברזני" className={styles.profilePhoto} loading="lazy" decoding="async" />
              </div>
            </div>
          </div>
        </div>

        <div className={styles.rightColumn}>
          <div className={styles.textSection}>
            
            <div className={`${styles.headerSection} ${styles.fadeInRight}`} ref={headerRef}>
              <span className={styles.greeting}>{t.about.greeting}</span>
              <h1 className={styles.name}>{t.about.name}</h1>
              <div className={styles.quoteLine}></div>
              <p className={styles.quote}>
                {t.about.quote}
              </p>
            </div>
            
            <div className={`${styles.story} ${styles.fadeInUp}`} ref={storyRef}>
              {isEnglish ? (
                <p>
                  I always knew I loved to design. I made a decision to follow my true passion. I studied interior design at <span className={styles.highlight}>Shenkar</span>, <span className={styles.highlight}>graduated with honors</span>, and became a lecturer there. For <span className={styles.highlight}>over 14 years</span> I've been working in interior planning and design. Homes I've designed have been featured in <span className={styles.highlight}>magazines in Israel and abroad</span>, <span className={styles.highlight}>on television and in Forbes</span>. Over the past decade I've guided <span className={styles.highlight}>hundreds of homes</span> and more importantly - hundreds of families to their dream home.
                </p>
              ) : (
                <p>
                  כל החיים ידעתי שאני אוהבת לעצב. קיבלתי החלטה ללכת אחרי התשוקה האמיתית שלי. הלכתי ללמוד עיצוב פנים ב<span className={styles.highlight}>שנקר</span>, <span className={styles.highlight}>סיימתי בהצטיינות</span>, והפכתי למרצה שם. כבר <span className={styles.highlight}>יותר מ-14 שנה</span> אני עוסקת בתכנון ועיצוב פנים. בתים שעיצבתי הופיעו ב<span className={styles.highlight}>מגזינים בארץ ובחו"ל</span>, <span className={styles.highlight}>בטלוויזיה ובפורבס</span>. מאז בעשור האחרון ליוויתי <span className={styles.highlight}>מאות בתים</span> ויותר חשוב -  מאות משפחות לבית החלומות שלהן
                </p>
              )}
              
              {/* First Divider - Parallax on mobile, regular on desktop */}
              {isMobile ? (
                <ParallaxDivider ref={parallax1Ref} image={migdal22} alt="דירת יוקרה אורבנית במגדל" />
              ) : (
                <RegularDivider />
              )}
              
              {isEnglish ? (
                <p>
                  What drives me every morning is my clients' faces at the end of the process when every piece of the puzzle comes together. I meet each client and dive deep into their world - their lifestyle, how they wake up in the morning, what they do before bed, where the shoes go, where the Dyson is, the coats, where they want the bags to be, what happens in each room and the first thing they do when they come home - and from there - I gather it all and create an <span className={styles.highlight}>organized, tidy, practical and designed home</span> that provides smart solutions for their needs.
                </p>
              ) : (
                <p>
                  מה שמניע אותי כל בוקר זה הפנים של הלקוחות שלי בסוף התהליך כשכל הפאזל מתחבר להם. אני פוגשת כל לקוח ולקוחה שלי ונכנסת איתם לעובי הקורה - על אורח החיים שלהם, על איך הם קמים בבוקר, מה הם עושים לפני השינה, איפה הנעליים נמצאות, איפה הדייסון, המעילים, איפה הם רוצים שהתיקים יהיו, מה עושים בכל חדר ומה הדבר הראשון שהם עושים ברגע שהם מגיעים הביתה - ומשם - אני אוספת את כל זה ויוצרת <span className={styles.highlight}>בית מאורגן, מסודר, פרקטי ומעוצב</span> שנותן פתרונות חכמים לצרכים שלהם.
                </p>
              )}
              
              {/* Second Divider - Parallax on mobile, regular on desktop */}
              {isMobile ? (
                <ParallaxDivider ref={parallax2Ref} image={ramatChen6} alt="בית פרטי ברמת חן" />
              ) : (
                <RegularDivider />
              )}
              
              {isEnglish ? (
                <p>
                  <span className={styles.highlightStrong}>My goal is simple:</span> a home that serves you - not the other way around. A home where there's <span className={styles.highlight}>a place for everything</span>, where you live in comfort and order, and where you truly <span className={styles.highlight}>feel at home</span>. My clients are with me <span className={styles.highlight}>hand in hand</span> - from the planning stage to the last picture on the wall. I make sure everything is <span className={styles.highlight}>planned and budgeted precisely</span>, bridging between all professionals, and guiding you through every step - from the initial vision to arranging the last accessory.
                </p>
              ) : (
                <p>
                  <span className={styles.highlightStrong}>המטרה שלי פשוטה:</span> בית שישרת אתכם - ולא להיפך. בית שבו יש <span className={styles.highlight}>מקום לכל דבר</span>, שבו אתם חיים בנוחות ובסדר, ושבו אתם באמת <span className={styles.highlight}>מרגישים בבית</span>. הלקוחות שלי איתי <span className={styles.highlight}>יד ביד</span> - מרגע התכנון ועד התמונה האחרונה על הקיר. אני דואגת שהכל יהיה <span className={styles.highlight}>מתוכנן ומתוקצב בצורה מדויקת</span>, מגשרת בין כל בעלי המקצוע, ומלווה אתכם בכל שלב - מהחזון הראשוני ועד סידור האקססוריז האחרון.
                </p>
              )}
            </div>
            
            <div className={styles.ctaContainer}>
              <a
                href={`https://wa.me/972544998929?text=${isEnglish ? 'Hi%20Narkis%2C%20I%20came%20from%20your%20website%20and%20would%20love%20to%20hear%20more%20about..' : 'היי%20נרקיס%20הגעתי%20מהדף%20שלך%20אשמח%20לשמוע%20עוד%20על..'}`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.ctaButton}
                style={{ textDecoration: 'none' }}
              >
                <span>{t.about.ctaText}</span>
                <svg className={styles.ctaArrow} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NarkisProfile;